/**
 * Franja de correo + bolsa en el popup de escaneo (depósito con dos correos, 2026-09-10).
 *
 *  - normal:            "CHILEXPRESS · Bolsa 0001" con el color del correo
 *  - sin bolsa:         amarilla "Escaneá una bolsa para Chilexpress" + campo para escanearla acá
 *  - correo desconocido: gris "Este envío no trae correo definido" + un botón por correo activo
 *  - correo desactivado: naranja "Este envío es de MailAmericas (correo desactivado)"
 * En modo simple sin datos de correo no se muestra nada.
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, Package } from 'lucide-react';
import type { BagGap, WhCourier, WhMode } from '../shared';

export function CourierBand({
  courier,
  courierUnknown,
  targetBag,
  bagGap,
  mode,
  couriers,
  chosenCourierKey,
  onChooseCourier,
  onBagOpened,
}: {
  courier: WhCourier | null | undefined;
  courierUnknown?: boolean;
  targetBag: { id: number; bag_code: string } | null | undefined;
  bagGap: BagGap | undefined;
  mode: WhMode | undefined;
  /** correos activos, para el caso "sin correo definido" */
  couriers: WhCourier[];
  chosenCourierKey: string | null;
  onChooseCourier: (key: string) => void;
  onBagOpened: (bag: { id: number; bag_code: string }) => void;
}) {
  const [bagInput, setBagInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!mode || mode === 'none') return null;

  const effectiveCourier: WhCourier | null =
    courier ?? (chosenCourierKey ? couriers.find((c) => c.key === chosenCourierKey) ?? null : null);

  // ── Correo desconocido y sin elección ──
  if (!effectiveCourier && (courierUnknown || bagGap === 'COURIER_REQUIRED')) {
    if (mode !== 'multi') return null;
    return (
      <div className="shrink-0 border-b border-[var(--color-border)] bg-gray-100 px-4 py-2.5 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-[var(--color-text)]">
            Este envío no trae correo definido. ¿De qué correo es?
          </span>
          {couriers
            .filter((c) => c.active)
            .map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onChooseCourier(c.key)}
                className="rounded-lg px-3 py-1.5 text-sm font-bold text-white hover:opacity-90"
                style={{ backgroundColor: c.color }}
              >
                Es {c.name}
              </button>
            ))}
        </div>
      </div>
    );
  }

  if (!effectiveCourier) return null;
  const color = effectiveCourier.color;

  // ── Sin bolsa abierta para este correo ──
  if (bagGap === 'NO_BAG_FOR_COURIER' || (mode === 'multi' && !targetBag)) {
    async function openBag(e: React.FormEvent) {
      e.preventDefault();
      const code = bagInput.trim();
      if (!code || busy) return;
      setBusy(true);
      setErr(null);
      try {
        const res = await fetch('/api/warehouse/bags/scan', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bag_code: code, courier_key: effectiveCourier!.key }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setErr(body?.error ?? 'No se pudo abrir la bolsa');
          return;
        }
        setBagInput('');
        onBagOpened({ id: body.bag.id, bag_code: body.bag.bag_code });
      } catch {
        setErr('Error de conexión al abrir la bolsa');
      } finally {
        setBusy(false);
      }
    }
    return (
      <div className="shrink-0 border-b border-yellow-300 bg-yellow-50 px-4 py-2.5 dark:border-yellow-700 dark:bg-yellow-900/30">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-bold text-yellow-900 dark:text-yellow-200">
            <AlertTriangle size={16} />
            Escaneá una bolsa para{' '}
            <span style={{ color }} className="uppercase">
              {effectiveCourier.name}
            </span>
          </span>
          <form onSubmit={openBag} className="flex flex-1 items-center gap-2 min-w-[220px]">
            <input
              autoFocus
              value={bagInput}
              onChange={(e) => setBagInput(e.target.value)}
              placeholder="Código de bolsa…"
              className="flex-1 rounded-lg border-2 border-yellow-400 bg-[var(--color-bg)] px-3 py-1.5 font-mono text-sm text-[var(--color-text)] outline-none focus:border-yellow-600"
            />
            <button
              type="submit"
              disabled={busy || !bagInput.trim()}
              className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: color }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
              Abrir
            </button>
          </form>
        </div>
        {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
      </div>
    );
  }

  // ── Correo desactivado (modo simple con paquete del otro correo) ──
  if (!effectiveCourier.active) {
    return (
      <div className="shrink-0 border-b border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-900 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-200">
        <AlertTriangle size={14} className="mr-1 inline" />
        Este envío es de {effectiveCourier.name} (correo desactivado)
        {targetBag ? (
          <>
            {' '}
            · va a la bolsa <span className="font-mono">{targetBag.bag_code}</span>
          </>
        ) : null}
      </div>
    );
  }

  // ── Normal ──
  if (mode !== 'multi' && !targetBag) return null;
  return (
    <div
      className="shrink-0 flex items-center justify-center gap-3 px-4 py-2 text-white"
      style={{ backgroundColor: color }}
    >
      <span className="text-base font-black uppercase tracking-widest">{effectiveCourier.name}</span>
      {targetBag && (
        <>
          <span className="opacity-70">·</span>
          <span className="text-base font-bold">
            Bolsa <span className="font-mono">{targetBag.bag_code}</span>
          </span>
        </>
      )}
    </div>
  );
}
